import * as stylex from '@stylexjs/stylex';
import { useMutation, useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { ArticleActionButtonGroup } from '@/components/article-action-button-group.component';
import { ArticleListItem } from '@/components/article-list-item.component';
import { ArticleList } from '@/components/article-list.component';
import { ARTICLE_SOURCE_SLOTS, ArticleSource } from '@/components/article-source.component';
import { ErrorState } from '@/components/error-state.component';
import { NotFoundState } from '@/components/not-found-state.component';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger.hook';
import { HttpException } from '@/libs/openapi';
import {
  Heading,
  Image,
  Link,
  Paragraph,
  ProgressIndicator,
  Skeleton,
  Text,
  useMediaQuery,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { RouterLink } from '@/libs/tanstack-router';
import { articleMutations, articleQueries } from '@/resources/article/article.api';
import { publicationQueries } from '@/resources/publication/publication.api';
import { tryCatch } from '@/utils/try-catch.util';

export const Route = createFileRoute('/_app/articles/$id')({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const { error } = await tryCatch(context.queryClient.ensureQueryData(articleQueries.view({ article_id: params.id })));
    if (error instanceof HttpException && error.response?.status === 404) {
      throw notFound();
    }
  },
  errorComponent: ErrorState,
  pendingComponent: PendingState,
  notFoundComponent: NotFoundState,
  head: () => ({
    meta: [
      {
        title: 'TrendMD | Article',
      },
    ],
  })
});

function RouteComponent() {
  const params = Route.useParams();
  const isMobile = useMediaQuery(breakpoints.small);

  const viewArticleQuery = useSuspenseQuery(articleQueries.view({ article_id: params.id }));
  const similarArticles = useSuspenseInfiniteQuery(articleQueries.similar({ article_id: params.id }));
  const publicationArticles = useSuspenseInfiniteQuery(publicationQueries.articles({
    publication_id: viewArticleQuery.data.publication_id!,
    page_size: 4,
  }));

  const saveArticleMutation = useMutation(articleMutations.save({
    publicationArticles: { publication_id: viewArticleQuery.data.publication_id!, page_size: 4, },
    similar: { article_id: params.id }
  }));
  const unsaveArticleMutation = useMutation(articleMutations.unsave({
    publicationArticles: { publication_id: viewArticleQuery.data.publication_id!, page_size: 4, },
    similar: { article_id: params.id }
  }));
  const excludeArticleMutation = useMutation(articleMutations.exclude({
    publicationArticles: { publication_id: viewArticleQuery.data.publication_id!, page_size: 4, },
    similar: { article_id: params.id }
  }));
  const undoExcludeArticleMutation = useMutation(articleMutations.removeExclude({
    publicationArticles: { publication_id: viewArticleQuery.data.publication_id!, page_size: 4, },
    similar: { article_id: params.id }
  }));
  const markAsSeenArticleMutation = useMutation(articleMutations.markAsSeen({
    publicationArticles: viewArticleQuery.data?.publication_id ? { publication_id: viewArticleQuery.data.publication_id, page_size: 4 } : undefined,
    similar: { article_id: params.id },
  }));

  useInfiniteScrollTrigger({ isFetching: similarArticles.isFetching, hasNextPage: similarArticles.hasNextPage, fetchNextPage: similarArticles.fetchNextPage });

  const hasMarkAsSeenFired = useRef(false);
  useEffect(() => {
    if (!hasMarkAsSeenFired.current && markAsSeenArticleMutation.isIdle && !viewArticleQuery.data?.seen) {
      hasMarkAsSeenFired.current = true;
      markAsSeenArticleMutation.mutate({ article_id: params.id });
    }

    return () => { hasMarkAsSeenFired.current = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, viewArticleQuery.data?.seen]);

  const similarArticlesItems = similarArticles.data?.pages?.flatMap((page) => page.articles) ?? [];
  const publicationArticlesItems = publicationArticles.data?.pages?.flatMap((page) => page.articles) ?? [];

  return (
    <>
      <div {...stylex.props(title.base)}>
        <Heading level={1} size={34}>{viewArticleQuery.data?.title}</Heading>
        <ArticleSource style={isMobile && mobileSourceStyles.base}>
          {isMobile ?
            <>
              <div {...stylex.props(mobileSourceStyles.container)}>
                <Image src={`https://www.google.com/s2/favicons?domain=${viewArticleQuery.data?.url}`} />
                <RouterLink params={{ id: viewArticleQuery.data.publication_id! }} to="/publishers/$id">
                  {viewArticleQuery.data?.publication_name}
                </RouterLink>
                {viewArticleQuery.data?.authors.map((author, index) =>
                  <Text key={author}>{author}{index < viewArticleQuery.data?.authors.length - 1 && ', '}</Text>)}
                <Text slot={ARTICLE_SOURCE_SLOTS.DATE_TEXT}>{viewArticleQuery.data?.publication_date}</Text>
              </div>
              <ArticleActionButtonGroup
                isExcluded={viewArticleQuery.data?.excluded}
                isSaved={viewArticleQuery.data?.saved}
                isVisible
                style={mobileSourceStyles.actionButtonGroup}
                onExcludePress={() => excludeArticleMutation.mutate({ article_id: params.id })}
                onSavePress={() => saveArticleMutation.mutate({ article_id: params.id })}
                onUndoExcludePress={() => undoExcludeArticleMutation.mutate({ article_id: params.id })}
                onUnsavePress={() => unsaveArticleMutation.mutate({ article_id: params.id })}
              />
            </> :
            <>
              <Image src={`https://www.google.com/s2/favicons?domain=${viewArticleQuery.data?.url}`} />
              <RouterLink params={{ id: viewArticleQuery.data.publication_id! }} to="/publishers/$id">
                {viewArticleQuery.data?.publication_name}
              </RouterLink>
              <Text>{viewArticleQuery.data?.authors.join(', ')}</Text>
              <Text slot={ARTICLE_SOURCE_SLOTS.DATE_TEXT}>{viewArticleQuery.data?.publication_date}</Text>
              <ArticleActionButtonGroup
                isExcluded={viewArticleQuery.data?.excluded}
                isSaved={viewArticleQuery.data?.saved}
                onExcludePress={() => excludeArticleMutation.mutate({ article_id: params.id })}
                onSavePress={() => saveArticleMutation.mutate({ article_id: params.id })}
                onUndoExcludePress={() => undoExcludeArticleMutation.mutate({ article_id: params.id })}
                onUnsavePress={() => unsaveArticleMutation.mutate({ article_id: params.id })}
              />
            </>}
        </ArticleSource>
        <Link href={viewArticleQuery.data.url!} target="_blank">
          {viewArticleQuery.data?.url}
        </Link>
      </div>

      <section {...stylex.props([sectionStyles.base, sectionStyles.abstract])}>
        <Heading size={24}>Abstract</Heading>
        <Paragraph color="secondary" size={20}>{viewArticleQuery.data?.abstract}</Paragraph>
      </section>

      {viewArticleQuery.data?.publication_id &&
        <section {...stylex.props([sectionStyles.base, sectionStyles.articles])}>
          <Heading level={2} size={28} style={headingStyles.articles}>More from {viewArticleQuery.data?.publication_name}</Heading>
          <ArticleList items={publicationArticlesItems}>
            {({ key, index, style, ref, ...item }) =>
              <ArticleListItem
                authors={item.authors}
                data-index={index}
                id={item.id}
                isExcluded={item.excluded}
                isLast={index === publicationArticlesItems.length - 1}
                isSaved={item.saved}
                isSeen={item.seen}
                key={key}
                publicationDate={item.publication_date}
                publicationId={item.publication_id}
                publicationName={item.publication_name}
                ref={ref}
                style={style}
                title={item.title}
                url={item.url}
                onExcludePress={() => excludeArticleMutation.mutate({ article_id: item.id })}
                onSavePress={() => saveArticleMutation.mutate({ article_id: item.id })}
                onUndoExcludePress={() => undoExcludeArticleMutation.mutate({ article_id: item.id })}
                onUnsavePress={() => unsaveArticleMutation.mutate({ article_id: item.id })}
              />}
          </ArticleList>
          {publicationArticles.hasNextPage &&
            <RouterLink _style={seeAllLinkStyles.base} params={{ id: viewArticleQuery.data?.publication_id }} to="/publishers/$id">
              See all from {viewArticleQuery.data?.publication_name}
            </RouterLink>}
        </section>}

      <section {...stylex.props([sectionStyles.base, sectionStyles.articles])}>
        <Heading level={2} size={28} style={headingStyles.articles}>More like this</Heading>
        <ArticleList items={similarArticlesItems}>
          {({ key, index, style, ref, ...item }) =>
            <ArticleListItem
              data-index={index}
              id={item.id}
              isExcluded={item.excluded}
              isLast={index === similarArticlesItems.length - 1}
              isSaved={item.saved}
              isSeen={item.seen}
              key={key}
              publicationDate={item.publication_date}
              publicationId={item.publication_id}
              publicationName={item.publication_name}
              ref={ref}
              style={style}
              title={item.title}
              url={item.url}
              onExcludePress={() => excludeArticleMutation.mutate({ article_id: item.id })}
              onSavePress={() => saveArticleMutation.mutate({ article_id: item.id })}
              onUndoExcludePress={() => undoExcludeArticleMutation.mutate({ article_id: item.id })}
              onUnsavePress={() => unsaveArticleMutation.mutate({ article_id: item.id })}
            />}
        </ArticleList>
        {!similarArticles.isLoading && similarArticles.hasNextPage && <ProgressIndicator size={24} style={progressIndicatorStyles.base} />}
      </section>
    </>
  );
}

function PendingState() {
  return (
    <>
      <div {...stylex.props(skeletonGroupStyles.base, skeletonGroupStyles.title)}>
        <Skeleton style={skeletonStyles.base({ width: 100 })} />
        <Skeleton style={skeletonStyles.base({ width: 100 })} />
        <Skeleton style={skeletonStyles.base({ width: 15 })} />
      </div>
      <div {...stylex.props(skeletonGroupStyles.base)}>
        <Skeleton style={skeletonStyles.base({ width: 92 })} />
        <Skeleton style={skeletonStyles.base({ width: 95 })} />
        <Skeleton style={skeletonStyles.base({ width: 100 })} />
        <Skeleton style={skeletonStyles.base({ width: 97 })} />
        <Skeleton style={skeletonStyles.base({ width: 85 })} />
      </div>
      <div {...stylex.props(skeletonGroupStyles.base)}>
        <Skeleton style={skeletonStyles.base({ width: 92 })} />
        <Skeleton style={skeletonStyles.base({ width: 95 })} />
        <Skeleton style={skeletonStyles.base({ width: 100 })} />
        <Skeleton style={skeletonStyles.base({ width: 97 })} />
        <Skeleton style={skeletonStyles.base({ width: 85 })} />
      </div>
      <div {...stylex.props(skeletonGroupStyles.base)}>
        <Skeleton style={skeletonStyles.base({ width: 92 })} />
        <Skeleton style={skeletonStyles.base({ width: 95 })} />
        <Skeleton style={skeletonStyles.base({ width: 100 })} />
        <Skeleton style={skeletonStyles.base({ width: 97 })} />
        <Skeleton style={skeletonStyles.base({ width: 85 })} />
      </div>
    </>
  );
}

const skeletonGroupStyles = stylex.create({
  base: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    justifyContent: 'flex-end',
    paddingInline: {
      default: 20,
      [breakpoints.large]: 0,
    },
  },
  title: {
    paddingBottom: 32,
    paddingTop: 48,
  }
});

const skeletonStyles = stylex.create({
  base: (props: { width: number }) => ({
    height: 20,
    width: `${props.width}%`,
  }),
});

const title = stylex.create({
  base: {
    backgroundColor: $.surfaceBGPrimary,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    height: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingInline: {
      default: 20,
      [breakpoints.large]: 0,
    },
    paddingTop: 48,
    width: {
      default: '100%',
      [breakpoints.large]: 728,
    },
  },
});

const mobileSourceStyles = stylex.create({
  base: {
    alignItems: 'flex-start',
    flexDirection: 'column',
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
  actionButtonGroup: {
    paddingLeft: 0,
  },
});

const seeAllLinkStyles = stylex.create({
  base: {
    paddingInline: {
      default: 20,
      [breakpoints.large]: 0,
    }
  },
});

const sectionStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
  },
  abstract: {
    alignSelf: 'stretch',
    gap: 8,
    paddingInline: {
      default: 20,
      [breakpoints.large]: 0,
    },
  },
  articles: {
    alignItems: 'flex-start',
    gap: {
      default: 8,
      [breakpoints.large]: 24,
    },
    height: '100%',
    paddingTop: {
      default: 48,
      [breakpoints.large]: 80,
    },
    width: '100%',
  }
});

const headingStyles = stylex.create({
  articles: {
    paddingInline: {
      default: 20,
      [breakpoints.large]: 0,
    },
  },
});

const progressIndicatorStyles = stylex.create({
  base: {
    alignSelf: 'center',
    marginBottom: 120,
    marginTop: 56,
  }
});
